<template>
    <div class="list-filter-bar">
        <!-- Each chip in a slot of its own, so the toolbar can measure it — even
             one it has set aside for want of room, which stays laid out, unseen. -->
        <div
            v-for="(item, index) in items"
            :key="item.key + (item.toggle ?? '')"
            class="list-filter-bar-slot"
            :class="{ 'is-overflowed': hidden.includes(index) }"
            :data-chip="index"
            :data-active="isChipActive(item) ? '1' : null"
        >
            <!-- A single answer worth one click: on or off, nothing to choose. -->
            <button
                v-if="item.toggle !== undefined"
                type="button"
                class="button is-small list-filter-chip"
                :class="{ 'is-active-filter': isToggleOn(item) }"
                @click="flipToggle(item)"
            >
                <b-icon v-if="item.icon" :icon="item.icon" size="is-small" />
                <span>{{ item.label }}</span>
            </button>

            <smart-dropdown v-else preferred-position="is-bottom-right">
                <template #trigger>
                    <button
                        type="button"
                        class="button is-small list-filter-chip"
                        :class="{ 'is-active-filter': isActive(item) }"
                    >
                        <span class="list-filter-chip-label">{{ item.label }} :</span>
                        <span class="list-filter-chip-value" :title="summaryOf(item)">{{ summaryOf(item) }}</span>
                        <a
                            v-if="isActive(item)"
                            class="list-filter-chip-clear"
                            :title="$t('Clear')"
                            @click.stop="write(item.qualifier, '')"
                        >
                            <b-icon icon="close" size="is-small" />
                        </a>
                        <b-icon v-else icon="chevron-down" size="is-small" />
                    </button>
                </template>

                <!-- A closed list: tick as many values as wanted. The menu stays open
                     while ticking — several statuses is the usual question. -->
                <template v-if="isChoice(item)">
                    <b-dropdown-item custom>
                        <a class="list-filter-bar-option" @click="write(item.qualifier, '')">
                            <b-icon icon="check" size="is-small" :class="{ 'is-invisible': isActive(item) }" />
                            {{ allLabelOf(item) }}
                        </a>
                    </b-dropdown-item>
                    <hr class="dropdown-divider" />
                    <b-dropdown-item v-for="option in choicesOf(item)" :key="option.value" custom>
                        <a class="list-filter-bar-option" @click="toggleChoice(item, option.value)">
                            <b-icon
                                icon="check"
                                size="is-small"
                                :class="{ 'is-invisible': !selectedOf(item).includes(option.value) }"
                            />
                            {{ option.label }}
                        </a>
                    </b-dropdown-item>
                </template>

                <!-- People: the org's users, held by the store, with « Moi » first —
                     resolved when the query runs, so a shared link means its reader. -->
                <template v-else-if="isPeople(item)">
                    <b-dropdown-item custom>
                        <b-input v-model="peopleSearch" size="is-small" icon="magnify" :placeholder="$t('Search...')" />
                    </b-dropdown-item>
                    <b-dropdown-item custom>
                        <a class="list-filter-bar-option" @click="write(item.qualifier, '')">
                            <b-icon icon="check" size="is-small" :class="{ 'is-invisible': isActive(item) }" />
                            {{ allLabelOf(item) }}
                        </a>
                    </b-dropdown-item>
                    <b-dropdown-item custom>
                        <a class="list-filter-bar-option" @click="write(item.qualifier, ME)">
                            <b-icon
                                icon="check"
                                size="is-small"
                                :class="{ 'is-invisible': selectedOf(item)[0] !== ME }"
                            />
                            {{ $t('Me') }}
                        </a>
                    </b-dropdown-item>
                    <hr class="dropdown-divider" />
                    <b-dropdown-item v-for="person in peopleOptions" :key="person.id" custom>
                        <a class="list-filter-bar-option" @click="write(item.qualifier, String(person.id))">
                            <b-icon
                                icon="check"
                                size="is-small"
                                :class="{ 'is-invisible': selectedOf(item)[0] !== String(person.id) }"
                            />
                            {{ person.name }}
                        </a>
                    </b-dropdown-item>
                </template>

                <!-- A date with declared shortcuts: one period at a time, a picked
                     day below for anything the shortcuts do not name. -->
                <template v-else-if="hasShortcuts(item)">
                    <b-dropdown-item custom>
                        <a class="list-filter-bar-option" @click="write(item.qualifier, '')">
                            <b-icon icon="check" size="is-small" :class="{ 'is-invisible': isActive(item) }" />
                            {{ allLabelOf(item) }}
                        </a>
                    </b-dropdown-item>
                    <hr class="dropdown-divider" />
                    <b-dropdown-item v-for="option in choicesOf(item)" :key="option.value" custom>
                        <a class="list-filter-bar-option" @click="pickShortcut(item, option.value)">
                            <b-icon
                                icon="check"
                                size="is-small"
                                :class="{ 'is-invisible': rawOf(item) !== option.value }"
                            />
                            {{ option.label }}
                        </a>
                    </b-dropdown-item>
                    <hr class="dropdown-divider" />
                    <b-dropdown-item custom>
                        <div class="list-filter-bar-field">
                            <filter-value-control
                                :qualifier="item.qualifier"
                                :operator="operatorOf(item)"
                                :model-value="isShortcut(item) ? '' : valueOf(item)"
                                @update:model-value="value => onValueInput(item, value)"
                            />
                        </div>
                    </b-dropdown-item>
                </template>

                <!-- Anything else: the control the menu and the query builder use. -->
                <b-dropdown-item v-else custom>
                    <div class="list-filter-bar-field">
                        <filter-value-control
                            :qualifier="item.qualifier"
                            :operator="operatorOf(item)"
                            :model-value="valueOf(item)"
                            @update:model-value="value => onValueInput(item, value)"
                        />
                    </div>
                </b-dropdown-item>
            </smart-dropdown>
        </div>
        <!-- What the room left out, counted; the « Filtrer » menu holds every one.
             Always laid out so its width is known before it is needed. -->
        <div class="list-filter-bar-slot" :class="{ 'is-overflowed': !hidden.length }" data-more>
            <button
                type="button"
                class="button is-small list-filter-chip"
                :class="{ 'is-active-filter': hidesActiveChip }"
                :title="hiddenLabels"
                @click.stop="$emit('more')"
            >
                +{{ Math.max(hidden.length, 1) }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import debounce from 'lodash/debounce';
import FilterValueControl from '@/components/base/FilterValueControl.vue';
import { useQualifierValues } from '@/composables/useQualifierValues';
import { useOrgStore } from '@/stores/org';
import { useUserStore } from '@/stores/user';
import { joinValue, operatorsOf, parseFilterValue, serializeFilterValue, splitValue } from '@/utils/filterOperators';

/**
 * The filter bar of a list: a few of its declared filters, laid out between the
 * list's title and its « Filtrer » button, one chip each.
 *
 * Nothing here filters on its own. A chip reads and writes the very URL
 * parameter its qualifier declares — the one the « Filtrer par » menu and the
 * search bar's chips use — so the three always show the same state, and a
 * filter set here is cleared by « Effacer les filtres » like any other.
 *
 * A list chooses which of its filters stand in the bar; the bar knows none of
 * them by name. Each entry is a qualifier key, or an object:
 *
 *   { key: 'prospect_status', label: 'Statut' }        a shorter label
 *   { key: 'prospect_priority', allLabel: 'Toutes' }   « Tous » agreeing with the label
 *   { key: 'follow_up', toggle: 'none', icon: '…' }     one value, on or off
 *
 * A key the list does not declare — or one this user may not read — is simply
 * not drawn: the descriptor is the authority on what can be filtered.
 */
const props = defineProps({
    // Ordered qualifier descriptors of the list (BaseList's orderedFilters).
    filters: {
        type: Array,
        default: () => []
    },
    // What stands in the bar, in this order.
    fields: {
        type: Array,
        default: () => []
    },
    // The chips set aside for want of room, by position — decided by the toolbar,
    // which knows the room (BaseList's measureToolbar).
    hidden: {
        type: Array,
        default: () => []
    }
});

defineEmits(['more']);

// The value the server resolves to the person reading (App\Search\Query\ValueResolver).
const ME = '@me';

const IS_ONE_OF = 'is_one_of';

const { proxy } = getCurrentInstance();
const route = useRoute();
const router = useRouter();
const orgStore = useOrgStore();
const userStore = useUserStore();
const { labelFor, cachedLabel, describeCondition } = useQualifierValues();

const items = computed(() =>
    props.fields
        .map(field => (typeof field === 'string' ? { key: field } : field))
        .map(field => {
            const qualifier = props.filters.find(filter => filter.key === field.key);

            if (!qualifier) {
                return null;
            }

            return {
                ...field,
                qualifier,
                label:
                    field.label || (field.toggle !== undefined ? toggleLabel(qualifier, field.toggle) : qualifier.label)
            };
        })
        .filter(Boolean)
);

/** A chip holding a filter is the last one set aside: it hides what the list is showing. */
function isChipActive(item) {
    return item.toggle !== undefined ? isToggleOn(item) : isActive(item);
}

/** « +N » carries a held filter's colour when one stepped aside behind it. */
const hidesActiveChip = computed(() => props.hidden.some(index => items.value[index] && isChipActive(items.value[index])));

const hiddenLabels = computed(() =>
    props.hidden
        .map(index => items.value[index]?.label)
        .filter(Boolean)
        .join(', ')
);

function toggleLabel(qualifier, value) {
    return (qualifier.enum_values || []).find(option => String(option.value) === String(value))?.label ?? value;
}

/** « Tous » by default; a feminine label (« Priorité ») brings its own « Toutes ». */
function allLabelOf(item) {
    return item.allLabel || proxy.$t('All');
}

function rawOf(item) {
    return String(route.query[item.qualifier.key] ?? '');
}

function operatorOf(item) {
    return parseFilterValue(rawOf(item), item.qualifier).operator;
}

function valueOf(item) {
    return parseFilterValue(rawOf(item), item.qualifier).value;
}

function isActive(item) {
    return rawOf(item) !== '';
}

function declares(qualifier, token) {
    return operatorsOf(qualifier).some(operator => operator.value === token);
}

/** A closed list of values, of any length: the bar's chips are dropdowns anyway. */
function isChoice(item) {
    const type = item.qualifier.type;

    return (type === 'enum' || type === 'boolean') && (item.qualifier.enum_values?.length ?? 0) > 0;
}

function hasShortcuts(item) {
    return item.qualifier.type === 'date' && (item.qualifier.enum_values?.length ?? 0) > 0;
}

function isShortcut(item) {
    return hasShortcuts(item) && choicesOf(item).some(option => option.value === rawOf(item));
}

/** One period at a time: picking the one already held clears it. */
function pickShortcut(item, value) {
    write(item.qualifier, rawOf(item) === value ? '' : value);
}

function isPeople(item) {
    return item.qualifier.type === 'relation' && item.qualifier.entity_type === 'employees';
}

function choicesOf(item) {
    return item.qualifier.enum_values.map(option => ({ value: String(option.value), label: option.label }));
}

/**
 * The values the chip holds as a selection — an exact value or a list of them.
 * Any other comparison (« n'est pas », written from the search bar) is not a
 * selection: the chip still names it, and picking a value replaces it.
 */
function selectedOf(item) {
    const { operator, value } = parseFilterValue(rawOf(item), item.qualifier);

    if (operator === IS_ONE_OF) {
        return splitValue(value);
    }

    return operator === '=' && value !== '' ? [value] : [];
}

function isSelection(item) {
    return !isActive(item) || selectedOf(item).length > 0;
}

/** What the chip reads as: « Tous », the picked labels, or the condition in words. */
function summaryOf(item) {
    if (!isActive(item)) {
        return allLabelOf(item);
    }

    if (!isSelection(item)) {
        return describeCondition(item.qualifier, rawOf(item));
    }

    return selectedOf(item)
        .map(value => labelOf(item, value))
        .join(', ');
}

function labelOf(item, value) {
    if (isChoice(item)) {
        return choicesOf(item).find(option => option.value === value)?.label ?? value;
    }

    if (value === ME) {
        return proxy.$t('Me');
    }

    // A relative date reads as the filter words it: « En retard », not « @overdue ».
    if (item.qualifier.type === 'date') {
        return item.qualifier.enum_values?.find(option => String(option.value) === value)?.label ?? value;
    }

    if (item.qualifier.type === 'relation') {
        return cachedLabel(item.qualifier, value) || value;
    }

    return value;
}

/**
 * Write one value or several: one travels bare, several as « est l'un de », so a
 * single pick reads in the URL exactly as the menu would have written it.
 */
function writeSelection(qualifier, values) {
    if (values.length > 1 && declares(qualifier, IS_ONE_OF)) {
        write(qualifier, serializeFilterValue(IS_ONE_OF, joinValue(values), qualifier));

        return;
    }

    write(qualifier, values[values.length - 1] ?? '');
}

function toggleChoice(item, value) {
    const current = isSelection(item) ? selectedOf(item) : [];
    const next = current.includes(value) ? current.filter(entry => entry !== value) : [...current, value];

    // Keep the declared order rather than the click order: the URL — and so a
    // saved view — reads the same whichever value was ticked first.
    const order = choicesOf(item).map(option => option.value);
    next.sort((a, b) => order.indexOf(a) - order.indexOf(b));

    writeSelection(item.qualifier, next);
}

function isToggleOn(item) {
    const selected = selectedOf(item);

    if (selected.length !== 1) {
        return false;
    }

    // « Mine » is on whether the link says `@me` or the reader's own id — an older
    // saved view or a copied URL carries the number.
    if (item.toggle === ME) {
        return selected[0] === ME || selected[0] === String(userStore.id);
    }

    return selected[0] === String(item.toggle);
}

function flipToggle(item) {
    write(item.qualifier, isToggleOn(item) ? '' : String(item.toggle));
}

/** Write the URL parameter as given — already serialized, or '' to clear it. */
function write(qualifier, serialized) {
    const query = { ...route.query };

    if (serialized === '') {
        delete query[qualifier.key];
    } else {
        query[qualifier.key] = serialized;
    }

    // A filtered list starts over at its first page.
    delete query.page;

    router.push({ name: route.name, params: route.params, query });
}

// Typing filters as you type would push a route per keystroke.
const writeDebounced = debounce((item, value) => writeValue(item, value), 400);

function writeValue(item, value) {
    write(item.qualifier, serializeFilterValue(operatorOf(item), value, item.qualifier));
}

function onValueInput(item, value) {
    if (item.qualifier.type === 'text' || item.qualifier.type === 'number') {
        writeDebounced(item, value);

        return;
    }

    writeValue(item, value);
}

// ── People ────────────────────────────────────────────────────────────────────
const peopleSearch = ref('');

const peopleOptions = computed(() => {
    const needle = peopleSearch.value.trim().toLowerCase();
    const people = (orgStore.employees || []).filter(person => person.name);

    return needle ? people.filter(person => person.name.toLowerCase().includes(needle)) : people;
});

// The store loads them once for the whole page, and only when a bar asks.
watch(
    () => items.value.some(isPeople),
    hasPeople => {
        if (hasPeople) {
            orgStore.loadEmployees();
        }
    },
    { immediate: true }
);

// An id already in the URL reads as a name, not « 1234 ».
watch(
    [items, () => route.query],
    async () => {
        for (const item of items.value) {
            if (item.qualifier.type !== 'relation') {
                continue;
            }

            for (const value of selectedOf(item)) {
                if (value !== ME) {
                    await labelFor(item.qualifier, value);
                }
            }
        }
    },
    { immediate: true, deep: true }
);
</script>

<style scoped>
.list-filter-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
    min-width: 0;
}
.list-filter-bar-slot {
    display: flex;
}
/* Set aside for want of room: out of the flow and unseen, yet still measured. */
.list-filter-bar-slot.is-overflowed {
    position: absolute;
    visibility: hidden;
    pointer-events: none;
}
.list-filter-chip {
    gap: 0.3rem;
    max-width: 20rem;
}
.list-filter-chip-label {
    font-weight: 600;
}
.list-filter-chip-value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.list-filter-chip.is-active-filter {
    border-color: var(--bulma-primary);
    background-color: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.12);
    color: var(--bulma-text-strong);
}
.list-filter-chip-clear {
    display: inline-flex;
    color: inherit;
}
.list-filter-bar-option {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: inherit;
    white-space: nowrap;
}
.list-filter-bar-field {
    min-width: 15rem;
}
</style>
